import { useEffect, useState } from "preact/hooks";
import type { FlowState } from "../flow/machine";
import { ERASE_ALL_FORWARDING_CODE, type InstallRunner } from "../flow/runner";
import { t } from "../i18n";
import { JobProgress } from "./GroupWidgets";
import { EsimNote } from "./SimChecklist";

/**
 * Remove a member (no phone connected). The cloud re-points the predecessor first, then
 * erases the removed member's forwarding and waits for read-backs; only then is a wipe or
 * unlock-for-return allowed. Dead phone: the customer dials ##002# on that SIM and confirms.
 */
export function RemoveStep({ runner, s }: { runner: InstallRunner; s: FlowState }) {
  const r = s.removal;
  const [cleared, setCleared] = useState(false);
  const [busy, setBusy] = useState(false);
  const jobId = r?.jobId;
  const jobDone = r?.job?.state === "done";
  useEffect(() => {
    if (jobId && !jobDone && !runner.store.state.error) void runner.pollRemovalJob();
  }, [runner, jobId, jobDone]);
  if (!r) return null;
  const confirm = async () => {
    setBusy(true);
    await runner.confirmCleared();
    setBusy(false);
  };
  const code = r.clearInstruction ?? ERASE_ALL_FORWARDING_CODE;
  const positions = [r.position - 1, r.position, r.position + 1].filter((p) => p >= 1 && p <= r.group.memberCount);
  return (
    <div class="panel">
      <h2 tabIndex={-1}>{t("remove.heading", { group: r.group.name, position: r.position })}</h2>
      <p>{t("remove.intro")}</p>
      <ol class="hint">
        <li>{t("remove.order1")}</li>
        <li>{r.force ? t("remove.order2_force") : t("remove.order2")}</li>
        <li>{t("remove.order3")}</li>
      </ol>
      <EsimNote where="remove" />
      <JobProgress job={r.job} positions={positions} />
      {r.force && jobDone && !r.done && (
        <section class="notice warn" aria-labelledby="clear-h">
          <h3 id="clear-h">{t("remove.dead_heading")}</h3>
          <p>{t("remove.dead_body")}</p>
          <p class="token ltr">{code}</p>
          <p class="hint">{t("remove.dead_hint")}</p>
          <label style="font-weight:400">
            <input type="checkbox" checked={cleared} onChange={(e) => setCleared((e.target as HTMLInputElement).checked)} /> {t("remove.dead_confirm_label", { code })}
          </label>
          <div class="row">
            <button type="button" class="primary" disabled={busy || !cleared} onClick={() => void confirm()}>{t("remove.dead_confirm_button")}</button>
          </div>
        </section>
      )}
      {r.done && (
        <section class="notice ok" role="status">
          <p>{t("remove.done", { position: r.position })}</p>
          <p class="hint">{t("remove.done_next")}</p>
          <button type="button" class="primary" onClick={() => runner.restartWithAnotherPhone()}>{t("remove.back")}</button>
        </section>
      )}
    </div>
  );
}
